import { useState } from "react";
import { evidenceItems } from "@/mocks/gossip";

const BEFORE_IMGS = [
  "https://readdy.ai/api/search-image?query=Dim%20dull%20lighting%20fitness%20gym%20scene%20photograph%20with%20slightly%20yellowish%20desaturated%20tones%2C%20weight%20training%20equipment%2C%20dark%20corners%2C%20low%20contrast%2C%20raw%20amateur%20photo%20feel%2C%20no%20visible%20person%20face&width=640&height=420&seq=gossip-ev-before1&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Plain%20dull%20desaturated%20beauty%20shot%20with%20visible%20imperfections%20and%20even%20flat%20lighting%2C%20bare%20skin%20texture%20closeup%2C%20unretouched%20raw%20photo%20feel%2C%20no%20face%2C%20soft%20grey%20background&width=640&height=420&seq=gossip-ev-before2&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Confusing%20airport%20boarding%20pass%20and%20ticket%20receipt%20documents%20on%20desk%2C%20dim%20cafe%20lighting%2C%20slightly%20blurred%20receipt%20text%2C%20dull%20overcast%20tones%2C%20no%20people%2C%20documentary%20photo%20style&width=640&height=420&seq=gossip-ev-before3&orientation=landscape",
];

const AFTER_IMGS = [
  "https://readdy.ai/api/search-image?query=Same%20fitness%20gym%20scene%20but%20bright%20vibrant%20enhanced%20tones%2C%20punchy%20warm%20golden%20highlights%2C%20saturated%20coral%20accents%2C%20clean%20professional%20marketing%20photo%2C%20no%20visible%20person%20face&width=640&height=420&seq=gossip-ev-after1&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Same%20beauty%20skin%20texture%20closeup%20but%20flawless%20smooth%20radiant%20airbrushed%2C%20bright%20warm%20glowing%20lighting%2C%20marketing%20campaign%20finish%2C%20no%20face%2C%20creamy%20background&width=640&height=420&seq=gossip-ev-after2&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Same%20airport%20ticket%20documents%20on%20desk%20but%20bright%20crisp%20clarity%2C%20sharp%20clear%20receipt%20text%20clearly%20readable%2C%20daylight%20clean%20tone%2C%20no%20people%2C%20documentary%20style&width=640&height=420&seq=gossip-ev-after3&orientation=landscape",
];

function CompareCard({ item, idx }: { item: (typeof evidenceItems)[number]; idx: number }) {
  const [pos, setPos] = useState(50);
  const before = BEFORE_IMGS[idx];
  const after = AFTER_IMGS[idx];

  return (
    <article className="rounded-3xl bg-background-100 border border-background-200 overflow-hidden hover:border-primary-300 transition-colors">
      <div className="p-4 md:p-5 pb-0">
        <span className="inline-flex items-center gap-2 text-[11px] font-medium text-primary-700">
          <i className="ri-arrow-left-right-line"></i> 前后对比 · 证据 {String(idx + 1).padStart(2, "0")}
        </span>
        <h3 className="mt-2 font-heading font-bold text-lg text-foreground-950">{item.title}</h3>
      </div>

      <div className="mt-4 relative select-none">
        <div className="relative w-full h-56 md:h-64 overflow-hidden">
          <img src={after} alt={item.afterNote} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 w-full overflow-hidden" style={{ width: `${pos}%` }}>
            <img src={before} alt={item.beforeNote} className="absolute inset-0 w-full h-full object-cover" style={{ width: "100%" }} />
          </div>
          <input
            type="range"
            min={0}
            max={100}
            value={pos}
            aria-label="拖动对比前后图片"
            onChange={(e) => setPos(Number(e.target.value))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
          />
          <div className="pointer-events-none absolute inset-y-0 flex items-center" style={{ left: `${pos}%` }}>
            <span className="flex items-center justify-center w-9 h-9 -ml-[18px] rounded-full bg-primary-500 text-background-50 shadow-none">
              <i className="ri-arrow-left-right-line text-sm"></i>
            </span>
          </div>
          <span className="absolute left-3 top-3 rounded-full bg-background-950/70 text-background-50 text-[11px] px-2.5 py-1">
            {item.beforeLabel}
          </span>
          <span className="absolute right-3 top-3 rounded-full bg-accent-500 text-background-50 text-[11px] px-2.5 py-1">
            {item.afterLabel}
          </span>
        </div>
      </div>

      <div className="p-4 md:p-5">
        <ul className="grid sm:grid-cols-2 gap-3 text-xs text-foreground-600">
          <li className="flex gap-2 rounded-xl bg-background-50 p-3">
            <i className="ri-arrow-up-circle-line text-secondary-600 shrink-0"></i>
            <span>{item.beforeNote}</span>
          </li>
          <li className="flex gap-2 rounded-xl bg-background-50 p-3">
            <i className="ri-arrow-down-circle-line text-accent-600 shrink-0"></i>
            <span>{item.afterNote}</span>
          </li>
        </ul>
        <p className="mt-3 flex gap-2 text-sm text-primary-700 bg-primary-50 rounded-xl p-3">
          <i className="ri-verified-badge-fill shrink-0"></i>
          <span><strong className="font-semibold">结论：</strong>{item.result}</span>
        </p>
      </div>
    </article>
  );
}

export default function EvidenceSection() {
  return (
    <section id="evidence" className="scroll-mt-20 bg-background-100 py-14 md:py-20 border-y border-background-200">
      <div className="mx-auto max-w-[1320px] px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <div className="max-w-2xl">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-primary-600">
              <span className="h-px w-8 bg-primary-400"></span> 证据对比
            </p>
            <h2 className="mt-3 font-heading font-black text-2xl md:text-4xl text-foreground-950">
              前后对比截图 · 拖动看差异
            </h2>
            <p className="mt-3 text-sm text-foreground-600">
              拖动画幅中间的滑块，逐帧比对素材细节。光线、色调、清晰度与时间戳的差异，往往就是还原真相的关键突破口。
            </p>
          </div>
          <span className="hidden md:inline-flex items-center gap-2 rounded-full bg-accent-100 text-accent-800 px-3.5 py-1.5 text-xs font-medium whitespace-nowrap">
            <i className="ri-arrow-left-right-line"></i> 拖动滑块对比
          </span>
        </div>

        <div className="mt-9 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {evidenceItems.map((item, idx) => (
            <CompareCard key={item.id} item={item} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}